/**
 * Schoolyear / Halbjahr configuration validation (OU-1f.1).
 *
 * Lives in `/shared` (CLAUDE.md §26) so the admin form (`SchoolyearsPage`) and
 * the authoritative backend route validate against ONE schema. Boundaries are
 * carried as date strings ("YYYY-MM-DD") — lexicographically orderable, so
 * cross-field ordering needs no Date parsing. The backend converts them to
 * Berlin-midnight instants (`combineDateTime`) for storage.
 *
 * termLabel convention: a Schoolyear `label` is "YYYY/YY" (start year / end year,
 * 2-digit), a term is "YYYY/YY-N" with N ∈ {1,2}. See ADR
 * `docs/decisions/schoolyear-und-halbjahr.md`.
 *   - "2025/26-1" → 1. Halbjahr (01.08.2025 – 31.01.2026)
 *   - "2025/26-2" → 2. Halbjahr (01.02.2026 – 31.07.2026)
 *   - "2026/27-1" → 1. Halbjahr des Folgejahres
 */
import { z } from 'zod';
export const SCHOOLYEAR_LABEL_REGEX = /^\d{4}\/\d{2}$/;
export const TERM_LABEL_REGEX = /^\d{4}\/\d{2}-[12]$/;
/** "2025/26" + 1 → "2025/26-1". */
export function deriveTermLabel(schoolyearLabel, halbjahr) {
    return `${schoolyearLabel}-${halbjahr}`;
}
/**
 * Reverse of `deriveTermLabel`: "2025/26-1" → "2025/26". Strips the trailing
 * Halbjahr suffix so a termLabel can be matched against a `Schoolyear.label`.
 * Symmetric to `deriveTermLabel` so the format contract stays in one place
 * (no fragile fixed-length slice in callers).
 */
export function deriveSchoolyearLabel(termLabel) {
    return termLabel.replace(/-[12]$/, '');
}
/**
 * NRW default boundaries for a schoolyear label. "2025/26" → 01.08.2025 …
 * 31.07.2026. Pure; used by the `/schoolyears/defaults` prefill endpoint and
 * the boot-/test-seed. Returns date strings ("YYYY-MM-DD").
 */
export function defaultSchoolyearBoundaries(label) {
    const startYear = Number(label.slice(0, 4));
    const endYear = startYear + 1;
    return {
        hj1Start: `${startYear}-08-01`,
        hj1End: `${endYear}-01-31`,
        hj2Start: `${endYear}-02-01`,
        hj2End: `${endYear}-07-31`,
    };
}
/** Whole-month span between two "YYYY-MM-DD" strings (sanity-band helper). */
function monthSpan(fromYmd, toYmd) {
    const [fy, fm] = [Number(fromYmd.slice(0, 4)), Number(fromYmd.slice(5, 7))];
    const [ty, tm] = [Number(toYmd.slice(0, 4)), Number(toYmd.slice(5, 7))];
    return (ty - fy) * 12 + (tm - fm);
}
const dateField = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum muss im Format YYYY-MM-DD sein.');
const schoolyearShape = {
    label: z
        .string()
        .regex(SCHOOLYEAR_LABEL_REGEX, 'Label muss im Format "YYYY/YY" sein, z. B. "2025/26".'),
    hj1Start: dateField,
    hj1End: dateField,
    hj2Start: dateField,
    hj2End: dateField,
};
function refineSchoolyear(v, ctx) {
    // AC #3 — strict chronological order.
    if (!(v.hj1Start < v.hj1End)) {
        ctx.addIssue({
            code: 'custom',
            path: ['hj1End'],
            message: 'HJ1-Ende muss nach HJ1-Start liegen.',
        });
    }
    if (!(v.hj1End < v.hj2Start)) {
        ctx.addIssue({
            code: 'custom',
            path: ['hj2Start'],
            message: 'HJ2-Start muss nach HJ1-Ende liegen.',
        });
    }
    if (!(v.hj2Start < v.hj2End)) {
        ctx.addIssue({
            code: 'custom',
            path: ['hj2End'],
            message: 'HJ2-Ende muss nach HJ2-Start liegen.',
        });
    }
    // AC #4 — range sanity: a school year spans roughly 6–14 months.
    const span = monthSpan(v.hj1Start, v.hj2End);
    if (span < 6 || span > 14) {
        ctx.addIssue({
            code: 'custom',
            path: ['hj2End'],
            message: 'Ein Schuljahr muss zwischen 6 und 14 Monaten umfassen.',
        });
    }
}
export const schoolyearCreateSchema = z.object(schoolyearShape).superRefine(refineSchoolyear);
export const schoolyearUpdateSchema = z.object(schoolyearShape).superRefine(refineSchoolyear);
//# sourceMappingURL=schoolyear-validation.js.map